// Everyone in the organization with their profile (photo, job title, status message, Do not disturb), loaded once
// and kept fresh by live "profile.updated" events. Any avatar given a user id shows that person's photo.
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "./api";

export type Status = { emoji: string | null; text: string; expiresAt: string | null };
export type Person = { id: string; name: string; avatarUrl: string | null; jobTitle: string; status: Status | null; dndUntil: string | null };

export const peopleKey = (slug: string) => ["people", slug] as const;
export const STATUS_EMOJIS = ["💬", "📅", "🎧", "🎯", "🏠", "🚗", "✈️", "🌴", "🍽️", "☕", "🤒", "👶", "📚", "🎉", "🔕", "🤔"];

const PeopleContext = createContext<Map<string, Person>>(new Map());

export function PeopleProvider({ slug, children }: { slug: string; children: ReactNode }) {
  const q = useQuery({ queryKey: peopleKey(slug), queryFn: () => api<{ people: Person[] }>(`/orgs/${slug}/people`), staleTime: 5 * 60_000 });
  const map = useMemo(() => new Map((q.data?.people ?? []).map((p) => [p.id, p])), [q.data]);
  return <PeopleContext.Provider value={map}>{children}</PeopleContext.Provider>;
}

export const usePeople = () => useContext(PeopleContext);
export const usePerson = (id: string | null | undefined) => useContext(PeopleContext).get(id ?? "");

// Re-renders every so often, so statuses and Do not disturb disappear when their time is up.
export function useNow(everyMs = 30_000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), everyMs); return () => clearInterval(t); }, [everyMs]);
  return now;
}

export const isDnd = (until: string | null | undefined, now = Date.now()) => Boolean(until && new Date(until).getTime() > now);
export const activeStatus = (s: Status | null | undefined, now = Date.now()) => (s && (!s.expiresAt || new Date(s.expiresAt).getTime() > now) ? s : null);
export const forever = (until: string) => new Date(until).getUTCFullYear() >= 9999;

// "until 14:30", "until tomorrow 09:00", "until Fri 09:00", or "until you turn it off".
export function untilText(until: string, youTurnOff = "until you turn it off"): string {
  if (forever(until)) return youTurnOff;
  const d = new Date(until);
  const now = new Date();
  const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const days = Math.round((new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime() - new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()) / 86_400_000);
  if (days <= 0) return `until ${time}`;
  if (days === 1) return `until tomorrow ${time}`;
  if (days < 7) return `until ${d.toLocaleDateString([], { weekday: "short" })} ${time}`;
  return `until ${d.toLocaleDateString([], { day: "numeric", month: "short" })}`;
}
